import { useState, useEffect, useCallback } from 'react';
import { X, Download } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mobile-catalog-11/artifacts/90v3fsl5_Logo%20Zurita%20-%20Logo%20Negro%20Eslogan%20%282%29.png";
const isDev = process.env.NODE_ENV === 'development';

export const InstallPWABanner = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Check if already installed (standalone mode)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
            || window.navigator.standalone 
            || document.referrer.includes('android-app://');

        if (isStandalone) {
            return;
        }

        // Check if user dismissed the banner before
        const dismissed = localStorage.getItem('pwa-banner-dismissed');
        if (dismissed) {
            const dismissedTime = parseInt(dismissed);
            const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
            if (daysSinceDismissed < 7) {
                return;
            }
        }

        // Detect iOS
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        setIsIOS(iOS);

        // Listen for the beforeinstallprompt event (Chrome, Edge, Samsung Internet)
        const handleBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShowBanner(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstall);

        // Show banner after delay
        const timer = setTimeout(() => {
            setShowBanner(true);
        }, 2000);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
            clearTimeout(timer);
        };
    }, []);

    const showInstallInstructions = useCallback(() => {
        if (isIOS) {
            alert('Para instalar Zurita App:\n\n1. Tocá el botón Compartir (📤) en la barra inferior\n2. Deslizá y seleccioná "Agregar a pantalla de inicio"\n3. Tocá "Agregar"');
        } else {
            // Android Chrome u otro navegador
            alert('Para instalar Zurita App:\n\n1. Tocá el menú (⋮) en la esquina superior derecha\n2. Seleccioná "Instalar aplicación" o "Agregar a pantalla de inicio"\n3. Confirmá la instalación');
        }
    }, [isIOS]);

    const handleInstall = useCallback(async () => {
        if (deferredPrompt) {
            // Trigger native install prompt directly
            try {
                deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                
                if (outcome === 'accepted') {
                    setShowBanner(false);
                    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
                }
                setDeferredPrompt(null);
            } catch (error) {
                if (isDev) console.error('Error al instalar:', error);
                showInstallInstructions();
            }
        } else {
            showInstallInstructions();
        }
    }, [deferredPrompt, showInstallInstructions]);

    const handleDismiss = useCallback(() => {
        setShowBanner(false);
        localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
    }, []);

    if (!showBanner) return null;

    return (
        <div 
            className="fixed top-0 left-0 right-0 z-[99998] bg-[#007a3f] text-white shadow-lg animate-slide-down pwa-banner-top safe-area-top"
            data-testid="pwa-install-banner"
        >
            <div className="app-container py-3">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1">
                        <div className="bg-white rounded-lg p-1">
                            <img 
                                src={LOGO_URL} 
                                alt="Zurita App" 
                                className="h-12 w-12 object-contain"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm">Instalá Zurita App</p>
                            <p className="text-xs text-white/80">Acceso rápido desde tu celular, recibí notificaciones y enterate de todas las novedades</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleInstall}
                            className="bg-white text-[#007a3f] px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-1.5 hover:bg-white/90 transition-colors"
                            data-testid="pwa-install-button"
                        >
                            <Download className="h-4 w-4" />
                            Instalar
                        </button>
                        <button
                            onClick={handleDismiss}
                            className="p-2 hover:bg-white/20 rounded-full transition-colors"
                            data-testid="pwa-dismiss-button"
                            aria-label="Cerrar"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
